import webCrypto from 'tiny-webcrypto';
export function base64Decode(str) {
    return Uint8Array.from(atob(str), c => c.charCodeAt(0)).buffer;
}
export function base64Encode(buff) {
    return btoa(String.fromCharCode(...new Uint8Array(buff)));
}
function isSymmetricKeyReference(val) {
    var _a;
    return !!((_a = val) === null || _a === void 0 ? void 0 : _a.keyAlias);
}
function isDerivedKeyReference(val) {
    var _a;
    return !!((_a = val) === null || _a === void 0 ? void 0 : _a.publicKeyAlias);
}
function isPasswordParams(val) {
    var _a;
    return !!((_a = (val)) === null || _a === void 0 ? void 0 : _a.password);
}
function isPasswordParamsWithSalt(val) {
    var _a, _b;
    return !!((_a = (val)) === null || _a === void 0 ? void 0 : _a.password) && !!((_b = (val)) === null || _b === void 0 ? void 0 : _b.salt);
}
export class KeyManager {
    constructor() {
        this.keyStore = null;
    }
    useKeyStore(keyStore) {
        this.keyStore = keyStore;
    }
    async checkAliasExists({ keyAlias }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#checkAliasExists: no keyStore is loaded`);
        }
        return {
            aliasExists: (await Promise.all([
                this.keyStore.getCryptoKey(keyAlias),
                this.keyStore.getCryptoKeyPair(keyAlias),
            ]))
                .some(Boolean)
        };
    }
    async generateKey({ keyAlias }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#generateKey: no keyStore is loaded`);
        }
        const key = await webCrypto.subtle.generateKey({
            name: 'AES-GCM',
            length: 256
        }, false, ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']);
        await this.keyStore.putCryptoKey(keyAlias, key);
    }
    async generateRecoverableSignatureKeyPair(keyWrapParams) {
        const keyPair = await webCrypto.subtle.generateKey({
            name: "ECDSA",
            namedCurve: "P-521",
        }, true, ["sign", "verify"]);
        const recoverableKeyPair = await this.keyPairToRecoverableKeyPair(keyPair, keyWrapParams);
        return { recoverableKeyPair };
    }
    async generateRecoverableAgreementKeyPair(keyWrapParams) {
        const keyPair = await webCrypto.subtle.generateKey({
            name: "ECDH",
            namedCurve: "P-521",
        }, true, ["deriveKey"]);
        const recoverableKeyPair = await this.keyPairToRecoverableKeyPair(keyPair, keyWrapParams);
        return { recoverableKeyPair };
    }
    async generateRecoverableKey(keyWrapParams) {
        const key = await webCrypto.subtle.generateKey({
            name: 'AES-GCM',
            length: 256
        }, true, ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']);
        return { recoverableKey: await this.keyToRecoverableKey(key, keyWrapParams) };
    }
    async rewrapSignatureKeyPair({ recoverableKeyPair, unwrapWith, rewrapWith }) {
        const [publicKey, privateKey] = await Promise.all([
            this.deserializePublicSignatureKey(recoverableKeyPair.publicKey),
            this.unwrapSignaturePrivateKey(recoverableKeyPair.privateKey, unwrapWith, true)
        ]);
        return {
            recoverableKeyPair: await this.keyPairToRecoverableKeyPair({
                publicKey,
                privateKey,
            }, rewrapWith)
        };
    }
    async rewrapAgreementKeyPair({ recoverableKeyPair, unwrapWith, rewrapWith }) {
        const [publicKey, privateKey] = await Promise.all([
            this.deserializePublicAgreementKey(recoverableKeyPair.publicKey),
            this.unwrapAgreementPrivateKey(recoverableKeyPair.privateKey, unwrapWith, true)
        ]);
        return {
            recoverableKeyPair: await this.keyPairToRecoverableKeyPair({
                publicKey,
                privateKey,
            }, rewrapWith)
        };
    }
    async rewrapKey({ recoverableKey, unwrapWith, rewrapWith }) {
        const key = await this.unwrapKey(recoverableKey, unwrapWith, true);
        return {
            recoverableKey: await this.keyToRecoverableKey(key, rewrapWith)
        };
    }
    async recoverSignatureKeyPair({ importAlias, recoverableKeyPair, unwrapWith }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#recoverSignatureKeyPair: no keyStore is loaded`);
        }
        const [publicKey, privateKey] = await Promise.all([
            this.deserializePublicSignatureKey(recoverableKeyPair.publicKey),
            this.unwrapSignaturePrivateKey(recoverableKeyPair.privateKey, unwrapWith, false)
        ]);
        const keyPair = {
            publicKey,
            privateKey
        };
        await this.keyStore.putCryptoKeyPair(importAlias, keyPair);
    }
    async recoverAgreementKeyPair({ importAlias, recoverableKeyPair, unwrapWith }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#recoverAgreementKeyPair: no keyStore is loaded`);
        }
        const [publicKey, privateKey] = await Promise.all([
            this.deserializePublicAgreementKey(recoverableKeyPair.publicKey),
            this.unwrapAgreementPrivateKey(recoverableKeyPair.privateKey, unwrapWith, false)
        ]);
        const keyPair = {
            publicKey,
            privateKey
        };
        await this.keyStore.putCryptoKeyPair(importAlias, keyPair);
    }
    async recoverKey({ importAlias, recoverableKey, unwrapWith }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#recoverKey: no keyStore is loaded`);
        }
        await this.keyStore.putCryptoKey(importAlias, await this.unwrapKey(recoverableKey, unwrapWith, false));
    }
    async importPublicSignatureKey({ alias, publicKey }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#importPublicSignatureKey: no keyStore is loaded`);
        }
        await this.keyStore.putCryptoKey(alias, await this.deserializePublicSignatureKey(publicKey));
    }
    async importPublicAgreementKey({ alias, publicKey }) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#importPublicAgreementKey: no keyStore is loaded`);
        }
        await this.keyStore.putCryptoKey(alias, await this.deserializePublicAgreementKey(publicKey));
    }
    async encrypt({ encryptWith, cleartext }) {
        const key = await this.resolveEncryptionKey(encryptWith);
        if (!key) {
            throw new Error('No encryption key could be resolved');
        }
        return {
            encryptedMessage: await this.encryptWithKey(cleartext, key)
        };
    }
    async decrypt({ decryptWith, encryptedMessage }) {
        const key = await this.resolveEncryptionKey(decryptWith);
        if (!key) {
            throw new Error('No decryption key could be resolved');
        }
        return {
            cleartext: await this.decryptWithKey(encryptedMessage, key)
        };
    }
    async sign({ keyAlias, cleartext }) {
        const keyPair = await this.resolveKeyPair(keyAlias);
        const enc = new TextEncoder();
        const params = { name: 'ECDSA', hash: 'SHA-256' };
        const sig = await webCrypto.subtle.sign(params, keyPair.privateKey, enc.encode(cleartext));
        return { signature: base64Encode(sig) };
    }
    async verify({ keyAlias, cleartext, signature }) {
        var _a;
        let publicKey;
        try {
            publicKey = await this.resolveKey(keyAlias);
            if (publicKey.type !== 'public') {
                throw new Error(`KeyManager#verify: resolved key ${keyAlias} is not a public key`);
            }
        }
        catch (err) {
            publicKey = (_a = (await this.resolveKeyPair(keyAlias))) === null || _a === void 0 ? void 0 : _a.publicKey;
        }
        if (!publicKey) {
            throw new Error(`KeyManager#verify: could not resolve key ${keyAlias}`);
        }
        const params = { name: 'ECDSA', hash: 'SHA-256' };
        const enc = new TextEncoder();
        const isValid = await webCrypto.subtle.verify(params, publicKey, base64Decode(signature), enc.encode(cleartext));
        return { isValid };
    }
    async resolveDerivedKey({ keyAlias, publicKeyAlias, info }) {
        const { privateKey, publicKey } = await this.resolveAgreementKeys(keyAlias, publicKeyAlias);
        return this.deriveEncryptionKey(privateKey, publicKey, info);
    }
    async resolveEncryptionKey(keyReference) {
        if (isDerivedKeyReference(keyReference)) {
            return this.resolveDerivedKey(keyReference);
        }
        else if (isSymmetricKeyReference(keyReference)) {
            return this.resolveKey(keyReference.keyAlias);
        }
        return null;
    }
    async resolveWrappingKey(unwrapParams) {
        const encKey = await this.resolveEncryptionKey(unwrapParams);
        if (encKey) {
            return encKey;
        }
        else if (isPasswordParamsWithSalt(unwrapParams)) {
            return await this.deriveKeyFromPassword(unwrapParams.password, base64Decode(unwrapParams.salt));
        }
        throw new Error('Unrecognized unwrapParams format');
    }
    addSaltToUnwrapParams(unwrapParams, recoverableKey) {
        if (isPasswordParams(unwrapParams)) {
            return Object.assign(Object.assign({}, unwrapParams), { salt: recoverableKey.salt });
        }
        return unwrapParams;
    }
    fillGeneratedSaltToUnwrapParams(unwrapParams) {
        if (isPasswordParams(unwrapParams)) {
            const { salt } = unwrapParams;
            return Object.assign(Object.assign({}, unwrapParams), { salt: salt !== null && salt !== void 0 ? salt : base64Encode(webCrypto.getRandomValues(new Uint8Array(128))) });
        }
        return unwrapParams;
    }
    async unwrapSignaturePrivateKey(recoverableKey, unwrapParams, extractable) {
        const wrappingKey = await this.resolveWrappingKey(this.addSaltToUnwrapParams(unwrapParams, recoverableKey));
        return webCrypto.subtle.unwrapKey('pkcs8', base64Decode(recoverableKey.ciphertext), wrappingKey, {
            name: 'AES-GCM',
            iv: base64Decode(recoverableKey.iv)
        }, {
            name: "ECDSA",
            namedCurve: "P-521",
        }, extractable, ['sign']);
    }
    async unwrapAgreementPrivateKey(recoverableKey, unwrapParams, extractable) {
        const wrappingKey = await this.resolveWrappingKey(this.addSaltToUnwrapParams(unwrapParams, recoverableKey));
        return webCrypto.subtle.unwrapKey('pkcs8', base64Decode(recoverableKey.ciphertext), wrappingKey, {
            name: 'AES-GCM',
            iv: base64Decode(recoverableKey.iv)
        }, {
            name: "ECDH",
            namedCurve: "P-521",
        }, extractable, ['deriveKey']);
    }
    async unwrapKey(recoverableKey, unwrapParams, extractable) {
        const wrappingKey = await this.resolveWrappingKey(this.addSaltToUnwrapParams(unwrapParams, recoverableKey));
        return webCrypto.subtle.unwrapKey('raw', base64Decode(recoverableKey.ciphertext), wrappingKey, { name: 'AES-GCM', iv: base64Decode(recoverableKey.iv) }, { name: 'AES-GCM', length: 256 }, extractable, ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']);
    }
    async wrapKeyWithKey(keyToWrap, wrappingKey) {
        const iv = webCrypto.getRandomValues(new Uint8Array(12));
        const key = await webCrypto.subtle.wrapKey(keyToWrap.algorithm.name == 'AES-GCM'
            ? 'raw'
            : 'pkcs8', keyToWrap, wrappingKey, {
            name: 'AES-GCM',
            iv
        });
        return { ciphertext: base64Encode(key), iv: base64Encode(iv.buffer) };
    }
    async deserializePublicSignatureKey(publicKey) {
        return webCrypto.subtle.importKey('spki', base64Decode(publicKey), {
            name: "ECDSA",
            namedCurve: "P-521",
        }, true, ['verify']);
    }
    async deserializePublicAgreementKey(publicKey) {
        return webCrypto.subtle.importKey('spki', base64Decode(publicKey), {
            name: "ECDH",
            namedCurve: "P-521",
        }, true, []);
    }
    async decryptWithKey(encryptedMessage, encryptionKey) {
        const decryptedBuff = await webCrypto.subtle.decrypt({
            name: 'AES-GCM',
            iv: base64Decode(encryptedMessage.iv)
        }, encryptionKey, base64Decode(encryptedMessage.ciphertext));
        const dec = new TextDecoder();
        return dec.decode(decryptedBuff);
    }
    async encryptWithKey(cleartext, encryptionKey) {
        const iv = webCrypto.getRandomValues(new Uint8Array(12));
        const enc = new TextEncoder();
        const encryptedBuffer = await webCrypto.subtle.encrypt({
            name: "AES-GCM",
            iv,
        }, encryptionKey, enc.encode(cleartext));
        return {
            iv: base64Encode(iv),
            ciphertext: base64Encode(encryptedBuffer)
        };
    }
    async resolveKey(keyAlias) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#resolveKey: no keyStore is loaded`);
        }
        const key = await this.keyStore.getCryptoKey(keyAlias);
        if (!key) {
            throw new Error(`No key with alias ${keyAlias} was found`);
        }
        return key;
    }
    async resolveAgreementKeys(privateKeyAlias, publicKeyAlias) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#resolveAgreementKeys: no keyStore is loaded`);
        }
        const keyPair = await this.keyStore.getCryptoKeyPair(privateKeyAlias);
        if (!keyPair) {
            throw new Error(`KeyManager#resolveAgreementKeys: key pair with alias ${privateKeyAlias} does not exist`);
        }
        const publicKey = privateKeyAlias === publicKeyAlias
            ? keyPair.publicKey
            : (await this.keyStore.getCryptoKey(publicKeyAlias));
        if (!publicKey) {
            throw new Error(`KeyManager#resolveAgreementKeys: public key with alias ${publicKeyAlias} does not exist`);
        }
        return {
            publicKey,
            privateKey: keyPair.privateKey
        };
    }
    async resolveKeyPair(keyAlias) {
        if (!this.keyStore) {
            throw new Error(`KeyManager#resolveKeyPair: no keyStore is loaded`);
        }
        const keyPair = await this.keyStore.getCryptoKeyPair(keyAlias);
        if (!keyPair) {
            throw new Error(`KeyManager#resolveKeyPair: key pair with alias ${keyAlias} does not exist`);
        }
        return keyPair;
    }
    async keyPairToRecoverableKeyPair(keyPair, wrapParams) {
        const params = this.fillGeneratedSaltToUnwrapParams(wrapParams);
        const wrappingKey = await this.resolveWrappingKey(params);
        const privateKey = await this.wrapKeyWithKey(keyPair.privateKey, wrappingKey);
        const publicKey = base64Encode(await webCrypto.subtle.exportKey('spki', keyPair.publicKey));
        if (isPasswordParamsWithSalt(params)) {
            const recoverableKeyPair = {
                privateKey: Object.assign(Object.assign({}, privateKey), { salt: params.salt }),
                publicKey
            };
            return recoverableKeyPair;
        }
        return {
            privateKey,
            publicKey
        };
    }
    async keyToRecoverableKey(key, wrapParams) {
        const params = this.fillGeneratedSaltToUnwrapParams(wrapParams);
        const wrappingKey = await this.resolveWrappingKey(params);
        const encryptedPrivateKey = await this.wrapKeyWithKey(key, wrappingKey);
        if (isPasswordParamsWithSalt(params)) {
            const recoverableKeyPair = Object.assign(Object.assign({}, encryptedPrivateKey), { salt: params.salt });
            return recoverableKeyPair;
        }
        return encryptedPrivateKey;
    }
    async deriveKeyFromPassword(password, salt) {
        const enc = new TextEncoder();
        const passwordKey = await webCrypto.subtle.importKey("raw", enc.encode(password), { name: "PBKDF2" }, false, ["deriveBits", "deriveKey"]);
        const derivedKey = await webCrypto.subtle.deriveKey({
            name: "PBKDF2",
            salt,
            iterations: 100000,
            hash: "SHA-256",
        }, passwordKey, { name: "AES-GCM", length: 256 }, false, ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']);
        return derivedKey;
    }
    async deriveEncryptionKey(privateKey, publicKey, info) {
        const agreedKey = await webCrypto.subtle.deriveKey({
            name: "ECDH",
            public: publicKey,
        }, privateKey, info != null
            ? {
                name: 'HKDF',
                length: 256
            }
            : {
                name: "AES-GCM",
                length: 256,
            }, false, info != null ? ["deriveKey"] : ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']);
        let encryptionKey = agreedKey;
        if (info != null) {
            const enc = new TextEncoder();
            encryptionKey = await webCrypto.subtle.deriveKey({
                name: "HKDF",
                salt: new Uint8Array(),
                info: enc.encode(info),
                hash: "SHA-256",
            }, agreedKey, { name: "AES-GCM", length: 256 }, false, ['wrapKey', 'unwrapKey', 'encrypt', 'decrypt']);
        }
        return encryptionKey;
    }
}
//# sourceMappingURL=key-manager.js.map